<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Monitor from "@/services/Monitor";
import Swal from "sweetalert2";

export default {
  components: { validationMessages },

  props: {
    competitionOptions: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      competition_id: "",
      tournament_name: "",
      tournament_season: "",
      tournament_start: "",
      tournament_end: "",
      tournament_id: "",
      submitted: false,
      tryingToSubmit: false,
    };
  },

  validations: {
    competition_id: { required },
    tournament_name: { required },
    tournament_season: { required },
    tournament_start: { required },
    tournament_end: { required },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_tournament");
    },

    formatError(error) {
      if (!error) return "Failed to create tournament";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    async createSportTournament() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        competition_id: this.competition_id,
        tournament_name: this.tournament_name,
        tournament_season: this.tournament_season,
        tournament_start: this.tournament_start,
        tournament_end: this.tournament_end,
        provider: this.provider,
      };

      if (this.tournament_id) payload.tournament_id = this.tournament_id;

      try {
        await Monitor.createSportTournament(payload);
        Swal.fire("Success!", "Tournament created successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.competition_id = "";
      this.tournament_name = "";
      this.tournament_season = "";
      this.tournament_start = "";
      this.tournament_end = "";
      this.tournament_id = "";
      this.submitted = false;
      this.$v.$reset();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_tournament"
    title="Add Tournament"
    title-class="font-18"
    centered
    size="lg"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportTournament">
      <div class="form-group mb-3">
        <label for="tournament_competition_id">Competition <span class="text-danger">*</span></label>
        <b-form-select
          id="tournament_competition_id"
          v-model="competition_id"
          :class="{ 'is-invalid': submitted && $v.competition_id.$error }"
        >
          <option value="">Select competition</option>
          <option
            v-for="item in competitionOptions"
            :key="item.competition_id"
            :value="item.competition_id"
          >
            {{ item.competition_name }} ({{ item.competition_id }})
          </option>
        </b-form-select>
        <validationMessages v-if="submitted" :field="$v.competition_id" />
      </div>

      <div class="form-group mb-3">
        <label for="tournament_name">Tournament Name <span class="text-danger">*</span></label>
        <input
          id="tournament_name"
          v-model="tournament_name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': submitted && $v.tournament_name.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.tournament_name" />
      </div>

      <div class="form-group mb-3">
        <label for="tournament_season">Tournament Season <span class="text-danger">*</span></label>
        <input
          id="tournament_season"
          v-model="tournament_season"
          type="text"
          class="form-control"
          placeholder="2024/2025"
          :class="{ 'is-invalid': submitted && $v.tournament_season.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.tournament_season" />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="tournament_start">Start Date <span class="text-danger">*</span></label>
            <input
              id="tournament_start"
              v-model="tournament_start"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': submitted && $v.tournament_start.$error }"
            />
            <validationMessages v-if="submitted" :field="$v.tournament_start" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="tournament_end">End Date <span class="text-danger">*</span></label>
            <input
              id="tournament_end"
              v-model="tournament_end"
              type="date"
              class="form-control"
              :class="{ 'is-invalid': submitted && $v.tournament_end.$error }"
            />
            <validationMessages v-if="submitted" :field="$v.tournament_end" />
          </div>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="tournament_id">Tournament ID</label>
        <input id="tournament_id" v-model="tournament_id" type="text" class="form-control" placeholder="Auto-generated if empty" />
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportTournament" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
